// DateRangePicker.jsx
import React, { useState, useCallback } from "react";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  Box,
  TextField,
  Typography,
  Button,
  Stack,
  Popover,
} from "@mui/material";
import dayjs from "dayjs";

const DateRangePicker = ({
  startLabel = "Start Date",
  endLabel = "End Date",
  defaultStartDate = null,
  defaultEndDate = null,
  onChange,
  format = "MMM D, YYYY",
  showQuickSelect = true,
  buttonText = "Select Date Range",
}) => {
  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleApply = () => {
    onChange?.({ startDate, endDate });
    handleClose();
  };

  const handleStartDateChange = useCallback(
    (newValue) => {
      setStartDate(newValue);
      if (newValue && endDate && newValue.isAfter(endDate)) {
        setEndDate(null);
      }
    },
    [endDate]
  );

  const handleEndDateChange = useCallback((newValue) => {
    setEndDate(newValue);
  }, []);

  const handleQuickSelect = useCallback(
    (days) => {
      const today = dayjs().startOf("day");
      let newStart = null;
      let newEnd = today;

      if (days === "reset") {
        newStart = defaultStartDate ?? null;
        newEnd = defaultEndDate ?? null;
      } else if (days === "today") {
        newStart = today;
        newEnd = today;
      } else if (days === "yesterday") {
        newStart = today.subtract(1, "day");
        newEnd = today.subtract(1, "day");
      } else {
        newStart = today.subtract(days - 1, "day");
      }

      setStartDate(newStart);
      setEndDate(newEnd);
      onChange?.({ startDate: newStart, endDate: newEnd });
    },
    [defaultStartDate, defaultEndDate, onChange]
  );

  const open = Boolean(anchorEl);
  const displayText =
    startDate && endDate
      ? `${startDate.format(format)} - ${endDate.format(format)}`
      : buttonText;

  return (
    <>
      <Button
        sx={{ px: 1, py: "6px" }}
        fullWidth
        variant="outlined"
        onClick={handleClick}
      >
        {displayText}
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <Box
          sx={{
            p: 2,
            width: { xs: "100%", sm: 400 },
            bgcolor: "background.paper",
            borderRadius: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          <Typography variant="h6" color="primary">
            Date Range
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <DatePicker
              label={startLabel}
              value={startDate}
              onChange={handleStartDateChange}
              format={format}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  size="small"
                  sx={{
                    flex: 1,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      bgcolor: "white",
                    },
                  }}
                />
              )}
            />
            <DatePicker
              label={endLabel}
              value={endDate}
              onChange={handleEndDateChange}
              minDate={startDate ?? undefined}
              format={format}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  size="small"
                  sx={{
                    flex: 1,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      bgcolor: "white",
                    },
                  }}
                />
              )}
            />
          </Stack>
          {showQuickSelect && (
            <Stack
              direction="row"
              spacing={1}
              sx={{ flexWrap: "wrap", gap: 1 }}
            >
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleQuickSelect("today")}
              >
                Today
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleQuickSelect("yesterday")}
              >
                Yesterday
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleQuickSelect(7)}
              >
                Last 7 Days
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleQuickSelect(30)}
              >
                Last 30 Days
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleQuickSelect("reset")}
              >
                Reset
              </Button>
            </Stack>
          )}
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button variant="outlined" size="small" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleApply}
              disabled={
                !startDate?.isValid() ||
                !endDate?.isValid() ||
                startDate.isAfter(endDate)
              }
            >
              Apply
            </Button>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Selected:{" "}
            {startDate && endDate
              ? `${startDate.format(format)} - ${endDate.format(format)}`
              : "Select both dates"}
          </Typography>
        </Box>
      </Popover>
    </>
  );
};

export default DateRangePicker;
